/**
 * Permission and Role Definitions for RBAC System
 *
 * Defines all available roles, permissions, and role-permission mappings
 * for the Admin Frontend RBAC (Role-Based Access Control) system.
 */

/**
 * User Roles Enum
 *
 * The portal has the same three roles as the backend role policy
 * (services/main-service/internal/auth/rolepolicy) and the Logto organization
 * role mapping (internal/auth/logto/mapper.go): viewer < manager < admin.
 * Any other role name is mapped by normalizeRole().
 * @enum {string}
 */
export const ROLES = {
  ADMIN: 'admin',
  MANAGER: 'manager',
  VIEWER: 'viewer',
};

/**
 * Role Hierarchy Levels
 * Higher numbers = higher privileges
 * @enum {number}
 */
export const ROLE_LEVELS = {
  [ROLES.VIEWER]: 1,
  [ROLES.MANAGER]: 2,
  [ROLES.ADMIN]: 3,
};

// Same mapping as the backend (mapLogtoRoleToAiRole in
// internal/auth/logto/mapper.go and src/utils/logtoRoles.ts): owner is an
// admin, and every other unknown name (cashier, the former reviewer, labeler
// and user roles, legacy operator/auditor accounts) is a viewer. super_admin
// is the legacy local-account admin.
const ROLE_ALIASES = {
  owner: ROLES.ADMIN,
  super_admin: ROLES.ADMIN,
};

/**
 * Map any role name to one of ROLES.
 * @param {string|null|undefined} role
 * @returns {string|null} null when no role is given
 */
export const normalizeRole = role => {
  if (!role) return null;
  const name = String(role).trim().toLowerCase();
  if (ROLE_LEVELS[name]) return name;
  return ROLE_ALIASES[name] || ROLES.VIEWER;
};

/**
 * Permission Categories
 * @enum {string}
 */
export const PERMISSION_CATEGORIES = {
  USER_MANAGEMENT: 'user_management',
  KYC_VERIFICATION: 'kyc_verification',
  CAMERA_MANAGEMENT: 'camera_management',
  HITL_MANAGEMENT: 'hitl_management',
  SYSTEM_ADMIN: 'system_admin',
  REPORTING: 'reporting',
  CHATBOT: 'chatbot',
  PRODUCT: 'product',
  SIGNAGE: 'signage',
  OTA: 'ota',
};

/**
 * Individual Permissions Enum
 * @enum {string}
 */
export const PERMISSIONS = {
  // User Management
  USER_READ: 'user:read',
  USER_WRITE: 'user:write',
  USER_DELETE: 'user:delete',
  USER_MANAGE_ROLES: 'user:manage_roles',

  // KYC Verification
  KYC_VERIFICATION_READ: 'kyc:verification:read',
  KYC_VERIFICATION_WRITE: 'kyc:verification:write',
  KYC_VERIFICATION_APPROVE: 'kyc:verification:approve',
  KYC_VERIFICATION_REJECT: 'kyc:verification:reject',

  // Camera Management
  CAMERA_READ: 'camera:read',
  CAMERA_WRITE: 'camera:write',
  CAMERA_DELETE: 'camera:delete',
  CAMERA_CONFIGURE: 'camera:configure',

  // HITL Management
  HITL_READ: 'hitl:read',
  HITL_DATA_MANAGE: 'hitl:data:manage',
  HITL_LABELING: 'hitl:labeling',
  HITL_QA_REVIEW: 'hitl:qa:review',
  HITL_TRAINING_MANAGE: 'hitl:training:manage',
  HITL_STATISTICS_VIEW: 'hitl:statistics:view',

  // System Administration
  SYSTEM_CONFIG_READ: 'system:config:read',
  SYSTEM_CONFIG_WRITE: 'system:config:write',
  SYSTEM_LOGS_READ: 'system:logs:read',
  SYSTEM_BACKUP: 'system:backup',
  SYSTEM_MAINTENANCE: 'system:maintenance',

  // Reporting
  REPORTS_READ: 'reports:read',
  REPORTS_EXPORT: 'reports:export',
  DASHBOARD_VIEW: 'dashboard:view',
  ANALYTICS_VIEW: 'analytics:view',

  // Chatbot
  CHATBOT_TEST: 'chatbot:test',
  CHATBOT_READ: 'chatbot:read',
  CHATBOT_MANAGE: 'chatbot:manage',

  // Product
  PRODUCT_READ: 'product:read',
  PRODUCT_MANAGE: 'product:manage',

  // Knowledge ingestion (names confirmed in DESIGN.md 0.2.0; enforcement is owned by the auth service)
  KNOWLEDGE_INGEST: 'knowledge:ingest',
  KNOWLEDGE_REVIEW: 'knowledge:review',
  KNOWLEDGE_PUBLISH: 'knowledge:publish',

  // Signage
  SIGNAGE_VIEW: 'signage:view',
  SIGNAGE_MANAGE: 'signage:manage',
  SIGNAGE_EMERGENCY: 'signage:emergency',

  // OTA updates (routes under /ota in App.tsx guard on these)
  OTA_READ: 'ota:read',
  OTA_WRITE: 'ota:write',
};

/**
 * Role-Permission Mappings
 * Each role inherits permissions from lower-level roles plus its own specific permissions
 * @type {Object<string, string[]>}
 */
export const ROLE_PERMISSIONS = {
  [ROLES.VIEWER]: [
    // Read-only
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.USER_READ,
    PERMISSIONS.KYC_VERIFICATION_READ,
    PERMISSIONS.CAMERA_READ,
    PERMISSIONS.HITL_STATISTICS_VIEW,
  ],

  [ROLES.MANAGER]: [
    // Viewer permissions plus day-to-day operations. The backend lets a
    // manager write everywhere except the admin-only areas, so nothing here
    // touches user administration, system, chatbot, knowledge, products, OTA
    // or signage emergencies.
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.USER_READ,
    PERMISSIONS.KYC_VERIFICATION_READ,
    PERMISSIONS.CAMERA_READ,
    PERMISSIONS.HITL_STATISTICS_VIEW,
    PERMISSIONS.HITL_READ,
    PERMISSIONS.HITL_LABELING,
    PERMISSIONS.HITL_DATA_MANAGE,
    PERMISSIONS.HITL_QA_REVIEW,
    PERMISSIONS.HITL_TRAINING_MANAGE,
    PERMISSIONS.KYC_VERIFICATION_WRITE,
    PERMISSIONS.KYC_VERIFICATION_APPROVE,
    PERMISSIONS.KYC_VERIFICATION_REJECT,
    PERMISSIONS.CAMERA_WRITE,
    PERMISSIONS.CAMERA_CONFIGURE,
    PERMISSIONS.REPORTS_READ,
    PERMISSIONS.REPORTS_EXPORT,
    PERMISSIONS.ANALYTICS_VIEW,
    PERMISSIONS.SIGNAGE_VIEW,
    PERMISSIONS.SIGNAGE_MANAGE,
  ],

  [ROLES.ADMIN]: [
    // Admins have all permissions by default
    ...Object.values(PERMISSIONS),
  ],
};

/**
 * Permission Groups for UI Organization
 * @type {Object<string, string[]>}
 */
export const PERMISSION_GROUPS = {
  [PERMISSION_CATEGORIES.USER_MANAGEMENT]: [
    PERMISSIONS.USER_READ,
    PERMISSIONS.USER_WRITE,
    PERMISSIONS.USER_DELETE,
    PERMISSIONS.USER_MANAGE_ROLES,
  ],

  [PERMISSION_CATEGORIES.KYC_VERIFICATION]: [
    PERMISSIONS.KYC_VERIFICATION_READ,
    PERMISSIONS.KYC_VERIFICATION_WRITE,
    PERMISSIONS.KYC_VERIFICATION_APPROVE,
    PERMISSIONS.KYC_VERIFICATION_REJECT,
  ],

  [PERMISSION_CATEGORIES.CAMERA_MANAGEMENT]: [
    PERMISSIONS.CAMERA_READ,
    PERMISSIONS.CAMERA_WRITE,
    PERMISSIONS.CAMERA_DELETE,
    PERMISSIONS.CAMERA_CONFIGURE,
  ],

  [PERMISSION_CATEGORIES.HITL_MANAGEMENT]: [
    PERMISSIONS.HITL_READ,
    PERMISSIONS.HITL_DATA_MANAGE,
    PERMISSIONS.HITL_LABELING,
    PERMISSIONS.HITL_QA_REVIEW,
    PERMISSIONS.HITL_TRAINING_MANAGE,
    PERMISSIONS.HITL_STATISTICS_VIEW,
  ],

  [PERMISSION_CATEGORIES.SYSTEM_ADMIN]: [
    PERMISSIONS.SYSTEM_CONFIG_READ,
    PERMISSIONS.SYSTEM_CONFIG_WRITE,
    PERMISSIONS.SYSTEM_LOGS_READ,
    PERMISSIONS.SYSTEM_BACKUP,
    PERMISSIONS.SYSTEM_MAINTENANCE,
  ],

  [PERMISSION_CATEGORIES.REPORTING]: [
    PERMISSIONS.REPORTS_READ,
    PERMISSIONS.REPORTS_EXPORT,
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.ANALYTICS_VIEW,
  ],

  [PERMISSION_CATEGORIES.SIGNAGE]: [
    PERMISSIONS.SIGNAGE_VIEW,
    PERMISSIONS.SIGNAGE_MANAGE,
    PERMISSIONS.SIGNAGE_EMERGENCY,
  ],

  [PERMISSION_CATEGORIES.OTA]: [PERMISSIONS.OTA_READ, PERMISSIONS.OTA_WRITE],
};

/**
 * Get all permissions for a role (including inherited permissions)
 * @param {string} role - The role to get permissions for
 * @returns {string[]} Array of permission strings
 */
export const getRolePermissions = role => {
  return ROLE_PERMISSIONS[normalizeRole(role)] || [];
};

/**
 * Get role level (higher number = higher privileges)
 * @param {string} role - The role to get level for
 * @returns {number} Role level (0 if not found)
 */
export const getRoleLevel = role => {
  return ROLE_LEVELS[normalizeRole(role)] || 0;
};

/**
 * Check if a role has a minimum level
 * @param {string} role - The role to check
 * @param {string} minRole - The minimum required role
 * @returns {boolean} True if role meets minimum requirement
 */
export const hasMinimumRole = (role, minRole) => {
  return getRoleLevel(role) >= getRoleLevel(minRole);
};

/**
 * Check if user has permission (considers role inheritance)
 * @param {Object} user - User object with roles and permissions
 * @param {string} permission - Permission to check
 * @returns {boolean} True if user has permission
 */
export const userHasPermission = (user, permission) => {
  if (!user) return false;

  // Check direct permissions first
  if (user.permissions?.includes(permission)) {
    return true;
  }

  // Check role-based permissions
  if (user.roles) {
    for (const role of user.roles) {
      const rolePermissions = getRolePermissions(role);
      if (rolePermissions.includes(permission)) {
        return true;
      }
    }
  }

  // Check single role (legacy support)
  if (user.role) {
    const rolePermissions = getRolePermissions(user.role);
    return rolePermissions.includes(permission);
  }

  return false;
};

/**
 * Check if user has any of the specified permissions
 * @param {Object} user - User object
 * @param {string[]} permissions - Array of permissions to check
 * @returns {boolean} True if user has any of the permissions
 */
export const userHasAnyPermission = (user, permissions) => {
  return permissions.some(permission => userHasPermission(user, permission));
};

/**
 * Check if user has all of the specified permissions
 * @param {Object} user - User object
 * @param {string[]} permissions - Array of permissions to check
 * @returns {boolean} True if user has all permissions
 */
export const userHasAllPermissions = (user, permissions) => {
  return permissions.every(permission => userHasPermission(user, permission));
};

/**
 * Get all permissions for a user (combines direct permissions and role permissions)
 * @param {Object} user - User object
 * @returns {string[]} Array of all user permissions
 */
export const getUserPermissions = user => {
  if (!user) return [];

  const permissions = new Set();

  // Add direct permissions
  if (user.permissions) {
    user.permissions.forEach(perm => permissions.add(perm));
  }

  // Add role-based permissions
  if (user.roles) {
    user.roles.forEach(role => {
      const rolePermissions = getRolePermissions(role);
      rolePermissions.forEach(perm => permissions.add(perm));
    });
  }

  // Legacy single role support
  if (user.role && !user.roles) {
    const rolePermissions = getRolePermissions(user.role);
    rolePermissions.forEach(perm => permissions.add(perm));
  }

  return Array.from(permissions);
};

/**
 * Get permissions by category for a user
 * @param {Object} user - User object
 * @param {string} category - Permission category
 * @returns {string[]} Permissions in the specified category
 */
export const getUserPermissionsByCategory = (user, category) => {
  const userPermissions = getUserPermissions(user);
  const categoryPermissions = PERMISSION_GROUPS[category] || [];

  return userPermissions.filter(perm => categoryPermissions.includes(perm));
};

// Default exports
export default {
  ROLES,
  ROLE_LEVELS,
  PERMISSIONS,
  PERMISSION_CATEGORIES,
  ROLE_PERMISSIONS,
  PERMISSION_GROUPS,
  normalizeRole,
  getRolePermissions,
  getRoleLevel,
  hasMinimumRole,
  userHasPermission,
  userHasAnyPermission,
  userHasAllPermissions,
  getUserPermissions,
  getUserPermissionsByCategory,
};
